import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiArrowRight, FiPhone, FiHeart, FiMapPin, FiTruck, FiShield, FiDollarSign, FiSearch, FiCheckCircle, FiCreditCard } from 'react-icons/fi';
import { useWishlist } from '../context/WishlistContext';
import { shedService } from '../services/shedService';

const DealerMap = lazy(() => import('../components/DealerMap'));

const IMG = '/assets/assets/images';

const HomeShed = () => {
  const [featuredSheds, setFeaturedSheds] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toggleWishlist, isWishlisted } = useWishlist();

  useEffect(() => {
    loadFeaturedSheds();
  }, []);

  const loadFeaturedSheds = async () => {
    try {
      const { data } = await shedService.getAvailableSheds();
      if (data) {
        setFeaturedSheds(data.slice(0, 6));
      }
    } catch (err) {
      console.error('Error loading sheds:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getRtoMonthly = (shed) => {
    const base = shed.salePrice && shed.salePrice < shed.price ? shed.salePrice : shed.price;
    return formatPrice(Math.round((base * 1.45) / 36));
  };

  const categories = [
    { name: 'Barn Sheds', href: 'barn', image: `${IMG}/12x24-custom-homeshed.webp`, count: 'Most popular' },
    { name: 'Workshops', href: 'workshop', image: `${IMG}/garage-shed-12x24.webp`, count: 'Heavy duty' },
    { name: 'Studio Sheds', href: 'studio', image: `${IMG}/10x20-studio-shed-2of4.webp`, count: 'Home office' },
    { name: 'Garages', href: 'garage', image: `${IMG}/12x24-garage-shed-2.webp`, count: 'Vehicle storage' },
    { name: 'Cabanas', href: 'cabana', image: `${IMG}/shed-with-deck.webp`, count: 'Outdoor living' },
    { name: 'Gable Sheds', href: 'gable', image: `${IMG}/12x24-a-frameshed.webp`, count: 'Classic style' },
  ];

  return (
    <>
      <Helmet>
        <title>Quality Storage Sheds, Barns & Workshops | ShedX</title>
        <meta name="description" content="Browse our selection of quality storage sheds, barns, workshops, and outdoor buildings. Free delivery and setup. Find your perfect shed today!" />
      </Helmet>

      {/* Hero — split layout with image + product messaging */}
      <section className="relative bg-gray-950 pt-16 overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-0 min-h-[85vh] items-center">
          {/* Left: copy */}
          <div className="py-16 lg:py-24 px-4 sm:px-6 lg:pl-[max(2rem,calc((100vw-1280px)/2+1rem))] lg:pr-12">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium tracking-wide uppercase px-3 py-1.5 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
              Sheds in stock &amp; ready to deliver
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1]">
              Shop Storage Sheds,<br />
              Barns &amp; Workshops
            </h1>
            <p className="text-lg text-gray-400 mb-8 max-w-lg">
              Browse real inventory from licensed dealers. Reserve online, get free delivery and professional setup. Rent-to-own available.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Link
                to="/sheds"
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 px-8 py-4 text-sm font-semibold hover:bg-gray-100 transition-colors"
              >
                <FiSearch className="w-4 h-4" />
                Shop all sheds
              </Link>
              <Link
                to="/shed-wanted"
                className="inline-flex items-center justify-center gap-2 border border-gray-600 text-gray-300 px-8 py-4 text-sm font-medium hover:border-gray-400 hover:text-white transition-colors"
              >
                Request a custom shed
                <FiArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {/* Quick stats */}
            <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
              <div className="flex items-center gap-2 text-gray-400">
                <FiTruck className="w-4 h-4 text-emerald-400" />
                Free delivery &amp; setup
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <FiDollarSign className="w-4 h-4 text-emerald-400" />
                From $89/mo RTO
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <FiShield className="w-4 h-4 text-emerald-400" />
                Warranty included
              </div>
            </div>
          </div>
          {/* Right: hero image — bleeds to viewport edge */}
          <div className="relative h-full min-h-[350px] lg:min-h-0">
            <img
              src={`${IMG}/shed-with-deck.webp`}
              alt="Custom-built shed with front deck and porch"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/40 to-transparent lg:block hidden" />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent lg:hidden" />
            {/* Price tag overlay */}
            <div className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-sm px-5 py-3 shadow-lg">
              <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-0.5">Starting at</p>
              <p className="text-2xl font-bold text-gray-900">$2,499</p>
              <p className="text-xs text-emerald-600 font-medium">or $89/mo with RTO</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
            {[
              { icon: FiTruck, label: 'Free Delivery', sub: 'Within 50 miles' },
              { icon: FiShield, label: '5-Year Warranty', sub: 'Manufacturer-backed' },
              { icon: FiCreditCard, label: 'Rent-to-Own', sub: 'No credit check' },
              { icon: FiCheckCircle, label: 'Licensed Dealers', sub: 'Vetted & insured' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 py-5 px-4">
                <item.icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-400">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shop by Category — image tiles */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex items-baseline justify-between mb-8">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Shop by category</h2>
              <p className="text-sm text-gray-400 mt-1">Find the right shed for your project</p>
            </div>
            <Link to="/sheds" className="text-sm text-gray-500 hover:text-gray-900 transition-colors hidden sm:inline">
              View all &rarr;
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.name}
                to={`/sheds?type=${cat.href}`}
                className="relative group overflow-hidden bg-gray-200 aspect-[4/3]"
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white font-semibold text-lg">{cat.name}</h3>
                  <p className="text-white/60 text-sm">{cat.count}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Inventory */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-baseline justify-between mb-8">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Featured sheds</h2>
              <p className="text-sm text-gray-400 mt-1">In stock and ready to ship</p>
            </div>
            <Link to="/sheds" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
              Shop all &rarr;
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-100 h-80 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredSheds.map((shed) => (
                <div key={shed.id} className="bg-white border border-gray-200 relative group hover:shadow-md transition-shadow">
                  <button
                    onClick={() => toggleWishlist(shed.id)}
                    className="absolute top-3 right-3 z-10 p-2 bg-white/90 hover:bg-white rounded-full shadow-sm transition-colors"
                  >
                    <FiHeart className={`w-4 h-4 ${isWishlisted(shed.id) ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />
                  </button>
                  <Link to={`/shed/${shed.id}`} className="block">
                    <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                      <img
                        src={shed.images?.[0] || '/assets/images/placeholder-shed.jpg'}
                        alt={shed.name}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 flex gap-1.5">
                        {shed.salePrice && shed.salePrice < shed.price && (
                          <span className="bg-red-600 text-white text-[11px] uppercase tracking-wider font-medium px-2.5 py-1">Sale</span>
                        )}
                        {shed.status === 'available' && (
                          <span className="bg-emerald-600 text-white text-[11px] uppercase tracking-wider font-medium px-2.5 py-1">In Stock</span>
                        )}
                      </div>
                      {shed.images?.length > 1 && (
                        <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[11px] px-2 py-0.5 rounded">
                          {shed.images.length} photos
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">
                        {shed.shedType} &middot; {shed.size}
                      </p>
                      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-emerald-700 transition-colors">
                        {shed.name}
                      </h3>
                      {shed.dealerName && (
                        <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                          <FiMapPin className="w-3 h-3" />
                          {shed.dealerName}{shed.dealerLocation ? ` — ${shed.dealerLocation}` : ''}
                        </p>
                      )}
                      <div className="flex items-baseline gap-2 mb-1">
                        {shed.salePrice && shed.salePrice < shed.price ? (
                          <>
                            <span className="text-lg font-bold text-red-600">
                              {formatPrice(shed.salePrice)}
                            </span>
                            <span className="text-sm text-gray-400 line-through">
                              {formatPrice(shed.price)}
                            </span>
                          </>
                        ) : (
                          <span className="text-lg font-bold text-gray-900">
                            {formatPrice(shed.price)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-emerald-600 font-medium">
                        or {getRtoMonthly(shed)}/mo with Rent-to-Own
                      </p>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Link
              to="/sheds"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3.5 text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              View all inventory
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-gray-950 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-semibold text-center mb-12">How buying a shed works</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: '1', title: 'Browse & compare', desc: 'Search real inventory by size, style and location. Compare up to 3 sheds side by side.' },
              { step: '2', title: 'Reserve online', desc: 'Secure your shed with a refundable deposit. Or apply for Rent-to-Own with no credit check.' },
              { step: '3', title: 'Free delivery & setup', desc: 'Your dealer delivers and installs your shed. Most orders arrive within 5-10 business days.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-600 text-white text-lg font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dealer Map */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Find a dealer near you</h2>
              <p className="text-sm text-gray-400 mt-1">Licensed dealers across the US</p>
            </div>
            <Link to="/dealers" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
              All dealers &rarr;
            </Link>
          </div>
          <Suspense fallback={<div className="h-[350px] bg-gray-100 animate-pulse" />}>
            <DealerMap compact className="h-[350px]" />
          </Suspense>
        </div>
      </section>

      {/* Shed Wanted CTA */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Don't see what you need?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Tell us what you're looking for. Size, style, budget — we'll match you with a dealer who can build it. No obligation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/shed-wanted"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-600 text-white px-7 py-3.5 text-sm font-semibold hover:bg-emerald-700 transition-colors"
                >
                  Submit a shed request
                  <FiArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="tel:+1"
                  className="inline-flex items-center justify-center gap-2 border border-gray-600 text-gray-300 px-7 py-3.5 text-sm font-medium hover:border-gray-400 hover:text-white transition-colors"
                >
                  <FiPhone className="w-4 h-4" />
                  Call us
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src={`${IMG}/12x24-custom-homeshed.webp`}
                alt="Custom-built storage shed"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomeShed;
